import t from 'react-translate';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { useForm, FormProvider } from 'react-hook-form';
import { useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  createCustomCommunication,
  updateCustomCommunication,
} from 'redux/actions/mentorship-program-custom-communications';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { gray6, info, primary } from 'styles/global_defaults/colors';
import {
  halfSpacing,
  largeSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import { TargetAudience } from 'redux/schemas/models/mentoring-programs';
import { CustomCommunicationMessageType } from 'redux/schemas/models/mentorship-program-custom-communications';
import { useSelector } from 'react-redux';
import { selectCustomCommunicationById } from 'redux/selectors/mentorship-program-custom-communications';
import { MentorshipProgramCustomCommunication } from 'redux/schemas/models/mentorship-program-custom-communications';
import { commsModalCommonStyles } from '../styles';
import { ModalMode } from '../..';
import { CustomCommunicationModalProps } from '..';
import DeliverySection from './delivery-section';
import SubjectInput from './subject-input';
import ContentEditor from './content-editor';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { getDefaultTemplate } from './default-templates';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { config } from '@config/pendo.config.json';

const INPUT_HEIGHT = 45;
const CUSTOM_COMMUNICATION_SUBJECT_MAX_LENGTH = 255;

const styles = css`
  .edit-communication-content {
    flex: 1;
    overflow-y: auto;

    .edit-communication-disabled {
      ${commsModalCommonStyles}
    }

    .edit-communication-delivery {
      ${commsModalCommonStyles}
      background: ${info};
      border-radius: ${halfSpacing}px;
      word-wrap: break-word;
      overflow-wrap: break-word;

      .athena-nv-select,
      .athena-nv-select button,
      div[role='button'] {
        width: 100% !important;
      }
      .athena-nv-select {
        height: ${INPUT_HEIGHT}px;
      }

      .required-indicator {
        pointer-events: none;
      }

      .message-type-tooltip {
        color: ${primary};
      }
    }
    .subject-wrapper {
      ${commsModalCommonStyles}
      padding: 0 1px;
    }
    .content-wrapper {
      ${commsModalCommonStyles}
    }
    .athena-input {
      height: ${INPUT_HEIGHT}px !important;
    }
  }

  /* Dropdown menu width */
  .athena-nv-select__menu,
  [class*='menu'] {
    width: 100% !important;
    min-width: 100% !important;
  }

  .edit-communication-footer {
    gap: ${threeQuartersSpacing}px;
    border-top: 1px solid ${gray6};
    padding: ${largeSpacing}px;
  }
`;

const EditCommunication = ({
  modalState,
  setModalState,
  registerCloseHandler,
  reset,
}: CustomCommunicationModalProps) => {
  const { mode, communicationId } = modalState;
  const isEditMode = mode === ModalMode.EDIT;
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: programId, matchingType } = mentoringProgram || {};
  const customCommunication: MentorshipProgramCustomCommunication = useSelector(
    selectCustomCommunicationById(communicationId)
  );
  const mentoringProgramAliases = useSelector((state) =>
      getMentoringProgramAliases(state, programId)
    );
  const { type, target, subject, body, enabled } = customCommunication || {};
  const translationBase = t.MENTORING_PROGRAMS.COMMUNICATIONS.COMMUNICATION_MODAL;
  const dataQATag = config.pendo.athena.mentorshipProgram.communications.editCommunicationModal;
  const successMessage = {
    [ModalMode.ADD]: translationBase.ADD_MODE.ADD_SUCCESS(),
    [ModalMode.EDIT]: translationBase.EDIT_MODE.UPDATE_SUCCESS(),
    [ModalMode.COPY]: translationBase.COPY_MODE.COPY_SUCCESS(),
  };

  const validationSchema = yup.object().shape({
    to: yup
      .mixed<TargetAudience>()
      .oneOf(Object.values(TargetAudience))
      .required(t.VALIDATION.REQUIRED()),
    messageType: yup
      .mixed<CustomCommunicationMessageType>()
      .oneOf(Object.values(CustomCommunicationMessageType))
      .required(t.VALIDATION.REQUIRED()),
    subject: yup
      .string()
      .required(t.VALIDATION.REQUIRED())
      .max(
        CUSTOM_COMMUNICATION_SUBJECT_MAX_LENGTH,
        translationBase.EDIT_MODE.ERRORS.SUBJECT(`${CUSTOM_COMMUNICATION_SUBJECT_MAX_LENGTH}`)
      ),
    content: yup.string().required(t.VALIDATION.REQUIRED()),
    enabled: yup.boolean(),
  });

  const defaultValues = useMemo(
    () => ({
      to: target,
      messageType: type,
      subject: subject || '',
      content: body || '',
      enabled: enabled || false,
    }),
    [target, type, subject, body, enabled]
  );

  const subjectInputRef = useRef<HTMLTextAreaElement>(null);

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const {
    formState: { isDirty, isSubmitting, isValid, dirtyFields },
    handleSubmit,
    watch,
    setValue,
  } = methods;

  const [toValue, messageTypeValue] = watch(['to', 'messageType']);

  const getNextModalState = useCallback(() => {
    if (modalState.previousMode) {
      return { mode: modalState.previousMode, communicationId, previousMode: null };
    }
    return { mode: null, communicationId: null };
  }, [modalState.previousMode, communicationId]);

  const handleCancel = useCallback(() => {
    const closeModal = () => setModalState(getNextModalState());

    if (isDirty) {
      dispatch(
        openConfirmationDialog({
          title: translationBase.EDIT_MODE.DISCARD_CHANGES.TITLE(),
          bodyText: translationBase.EDIT_MODE.DISCARD_CHANGES.BODY(),
          confirmText: t.FORM.DISCARD(),
          onConfirm: closeModal,
          cancelDataQa:
            dataQATag.discardCommunicationChangesConfirmation.cancelButton,
          confirmDataQa:
            dataQATag.discardCommunicationChangesConfirmation.confirmButton,
        })
      );
    } else {
      closeModal();
    }
  }, [isDirty, dispatch, translationBase, setModalState, getNextModalState]);

  useEffect(() => {
    registerCloseHandler?.(handleCancel);
  }, [registerCloseHandler, handleCancel]);

  useEffect(() => {
    if (mode === ModalMode.COPY && subjectInputRef.current && subject) {
      // Delay to ensure input is focused and React Hook Form has hydrated the field
      const timeoutId = setTimeout(() => {
        // Guard against mode change between setTimeout and execution
        if (mode === ModalMode.COPY) {
          subjectInputRef.current?.select();
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [mode, subject]);

  // Set default templates when TO and MESSAGE TYPE are selected in ADD mode
  useEffect(() => {
    // Only apply defaults in ADD mode
    if (mode !== ModalMode.ADD) return;

    // Both TO and MESSAGE TYPE must be selected
    if (!toValue || !messageTypeValue || !matchingType) return;

    // Get the default template
    const defaultTemplate = getDefaultTemplate(t, matchingType, messageTypeValue, toValue, mentoringProgramAliases);
    if (!defaultTemplate) return;

    // Set subject if hasn't been modified by user
    if (!dirtyFields.subject) {
      setValue('subject', defaultTemplate.subject, { shouldValidate: true, shouldDirty: false });
    }

    // Set content if hasn't been modified by user
    if (!dirtyFields.content) {
      setValue('content', defaultTemplate.body, { shouldValidate: true, shouldDirty: false });
    }
  }, [mode, toValue, messageTypeValue, matchingType, dirtyFields, setValue, mentoringProgramAliases]);

  const onSubmit = async (data: any) => {
    const payload = {
      programId,
      type: data.messageType,
      target: data.to,
      subject: data.subject,
      body: data.content,
      enabled: mode === ModalMode.ADD ? true : data.enabled,
    };

    const action = isEditMode
      ? updateCustomCommunication({
          ...payload,
          communicationId,
        })
      : createCustomCommunication({
          ...payload,
        });

    const {
      payload: createdCommunication,
    }: { payload: MentorshipProgramCustomCommunication } =
      await wrapThunkAction(dispatch(action));
    if (
      (mode === ModalMode.ADD || mode === ModalMode.COPY) &&
      createdCommunication
    ) {
      setModalState({
        mode: ModalMode.VIEW,
        communicationId: createdCommunication.id,
      });
      reset?.();
    } else {
      setModalState(getNextModalState());
    }
    dispatch(
      addAlertMessage({
        type: AlertMessageType.SUCCESS,
        header: t.FORM.SUCCESS_BANG(),
        message: successMessage[mode],
      })
    );
  };

  return (
    <div css={styles} className='h-100 w-100 d-flex flex-column'>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className='h-100 d-flex flex-column'
        >
          <div className='edit-communication-content d-flex flex-column align-items-center w-100 py-4'>
            {isEditMode && !enabled && (
              <div className='edit-communication-disabled pb-2 text-small color-gray-2 semi-bold text-right w-100'>
                {translationBase.DISABLED()}
              </div>
            )}
            <DeliverySection />
            <SubjectInput ref={subjectInputRef} />
            <ContentEditor messageType={messageTypeValue} />
          </div>
          <div className='edit-communication-footer w-100 d-flex justify-content-center'>
            <Button
              size='lg'
              variant='secondary'
              onClick={handleCancel}
              data-qa={dataQATag.cancelButton}
            >
              {t.FORM.CANCEL()}
            </Button>
            <Button
              size='lg'
              variant='primary'
              type='submit'
              disabled={!isValid || isSubmitting || (isEditMode && !isDirty)}
              data-qa={isEditMode ? dataQATag.saveButton : dataQATag.addButton}
            >
              {isSubmitting
                ? isEditMode
                  ? t.FORM.SAVING()
                  : t.FORM.ADDING()
                : isEditMode
                ? t.FORM.SAVE()
                : t.FORM.ADD()}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default EditCommunication;
